import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import broadcastService from '../../../services/whatsapp/broadcastService';
import {
  Broadcast,
  BroadcastRecipient,
  RecipientStatus
} from '../../../types/models/whatsapp';

interface RecipientsDialogProps {
  /** Null closes the dialog. */
  broadcast: Broadcast | null;
  onClose: () => void;
}

const STATUS_TONE: Record<RecipientStatus, string> = {
  pending: 'text-gray-500',
  sending: 'text-warning-600',
  sent: 'text-gray-700',
  delivered: 'text-primary-600',
  read: 'text-success-600',
  failed: 'text-danger-600',
  skipped: 'text-gray-400'
};

/**
 * 'responded' is not a RecipientStatus — a contact who replies keeps their
 * delivery status. The server filters it on the respondedAt timestamp.
 */
type RecipientFilter = RecipientStatus | 'all' | 'responded';

const FILTERS: Array<{ label: string; value: RecipientFilter }> = [
  { label: 'All', value: 'all' },
  { label: 'Waiting', value: 'pending' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Read', value: 'read' },
  { label: 'Replied', value: 'responded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Skipped', value: 'skipped' }
];

/**
 * Per-recipient status for one campaign.
 *
 * Fetched on demand rather than subscribed: a campaign can hold thousands of
 * rows, and a live listener over all of them would be costly for a view that
 * is opened occasionally.
 */
const RecipientsDialog: React.FC<RecipientsDialogProps> = ({
  broadcast,
  onClose
}) => {
  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);
  const [filter, setFilter] = useState<RecipientFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!broadcast) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    broadcastService
      .listRecipients(broadcast.id, filter === 'all' ? undefined : filter)
      .then((result) => {
        if (!cancelled) setRecipients(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load recipients');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [broadcast, filter]);

  if (!broadcast) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-gray-900">
              {broadcast.name}
            </h2>
            <p className="text-xs text-gray-500">
              {broadcast.stats.total} recipient
              {broadcast.stats.total === 1 ? '' : 's'} · {broadcast.templateName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 p-2">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                filter === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && (
            <p className="p-6 text-center text-xs text-gray-500">Loading…</p>
          )}

          {error && (
            <p className="p-6 text-center text-xs text-danger-600">{error}</p>
          )}

          {!loading && !error && recipients.length === 0 && (
            <p className="p-6 text-center text-xs text-gray-500">
              No recipients with this status.
            </p>
          )}

          {!loading &&
            recipients.map((recipient) => (
              <div
                key={recipient.id}
                className="flex items-center gap-3 border-b border-gray-100 px-4 py-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-gray-900">
                    {recipient.name || `+${recipient.phone}`}
                  </span>
                  <span className="block text-[11px] text-gray-500">
                    +{recipient.phone}
                    {recipient.error && (
                      <span className="text-danger-600"> · {recipient.error}</span>
                    )}
                  </span>
                </span>
                <span
                  className={`shrink-0 text-[11px] font-medium capitalize ${
                    STATUS_TONE[recipient.status]
                  }`}
                >
                  {recipient.status}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default RecipientsDialog;
